import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';
import logger from 'helpers/logger';

import locales from '../../../locales';
import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';
import { State } from './state';

const transitions = {} as Record<State, { target: string }>;

Object.values(State).forEach((stateNode) => {
  transitions[stateNode] = {
    target: stateNode,
  };
});

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'standard',

    initial: State.INIT,

    on: {
      ...transitions,
      '*': {
        actions: shared.actions.unhandled(),
      },
      'company-activity-outside-eu': State.PERSONAL_INFO,
      'company-has-sanctions': State.PERSONAL_INFO,
      'company-sanctions': State.PERSONAL_INFO,
      'has-foreign-fiscal-residence': State.PERSONAL_INFO,
      'shareholders-business-role': State.PERSONAL_INFO,
      'shareholders-only-corporate-officer': State.PERSONAL_INFO,
      'shareholders-personal-address': State.PERSONAL_INFO,
      'shareholders-personal-name': State.PERSONAL_INFO,
      'under-review': State.REVIEW,
      'user-agreement': State.AGREEMENT,
      'verification-address-check-document': State.VERIFICATION,
      'verification-address-other': State.VERIFICATION,
      'verification-address-proof-type': State.VERIFICATION,
      'verification-address-status': State.VERIFICATION,
      'verification-address-update': State.VERIFICATION,
      'verification-address-upload': State.VERIFICATION,
      'verification-company-papers-check-document': State.VERIFICATION,
      'verification-company-papers-info': State.VERIFICATION,
      'verification-company-papers-upload': State.VERIFICATION,
      'verification-guide': State.VERIFICATION,
      'verification-home': State.VERIFICATION,
      'verification-host-id-document': State.VERIFICATION,
      'verification-id-document': State.VERIFICATION,
      'verification-id-info': State.VERIFICATION,
      'verification-proof-of-accommodation': State.VERIFICATION,
      'verification-statutes-check-document': State.VERIFICATION,
      'verification-statutes-info': State.VERIFICATION,
      'verification-statutes-upload': State.VERIFICATION,
    },

    states: {
      [State.INIT]: {
        always: [
          /**
           * Checkpoints
           */
          {
            cond: Guard.IsAwaitingAccountValidation,
            target: State.REVIEW,
          },
          {
            cond: Guard.IsRefused,
            target: State.CANNOT_OPEN_ACCOUNT,
          },
          /**
           * Chronological steps
           */
          {
            cond: Guard.ShouldAnswerBusinessInfo,
            target: State.BUSINESS_INFO,
          },
          {
            cond: Guard.ShouldAnswerPersonalInfo,
            target: State.ONBOARDING_RESEARCH, // will transition to PERSONAL_INFO
          },
          {
            cond: Guard.ShouldAnswerCompanyCompliance,
            target: State.COMPANY_COMPLIANCE,
          },
          {
            target: State.VERIFICATION,
          },
        ],
      },

      [State.BUSINESS_INFO]: {
        on: {
          GO_CAPITAL_DEPOSIT: State.ABORT_TO_CAPITAL_DEPOSIT,
          NEXT: State.ONBOARDING_RESEARCH, // will transition to PERSONAL_INFO
          PREV: State.ABORT_APPLICATION,
        },
      },

      [State.ABORT_STANDARD_APPLICATION_PROMPT]: {
        entry: shared.actions.trackView(),

        on: {
          ABORT_APPLICATION: State.ABORT_APPLICATION,
          NEXT: State.INIT,
          PREV: State.ABORT,
        },
      },

      [State.ABORT_APPLICATION]: {
        invoke: {
          onDone: {
            target: State.DONE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.INIT,
          },
          src: Service.AbortApplication,
        },
      },

      [State.ONBOARDING_RESEARCH]: {
        on: {
          GO_RESET_PROMPT: State.ABORT_STANDARD_APPLICATION_PROMPT,
          NEXT: State.PERSONAL_INFO,
          PREV: State.ABORT,
        },
      },

      [State.PERSONAL_INFO]: {
        on: {
          GO_RESET_PROMPT: State.ABORT_STANDARD_APPLICATION_PROMPT,
          NEXT: [
            {
              cond: Guard.ShouldAnswerCompanyCompliance,
              target: State.COMPANY_COMPLIANCE,
            },
            { target: State.USER_PROFILE_SEND },
          ],
          PREV: State.BUSINESS_INFO,
        },
      },

      [State.COMPANY_COMPLIANCE]: {
        on: {
          NEXT: State.USER_PROFILE_SEND,
          PREV: State.PERSONAL_INFO,
        },
      },

      [State.USER_PROFILE_SEND]: {
        invoke: {
          onDone: {
            target: State.VERIFICATION,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.INIT,
          },
          src: Service.FinishProfileSetup,
        },
      },

      [State.VERIFICATION]: {
        always: {
          cond: Guard.IsAwaitingAccountValidation,
          target: State.REVIEW,
        },

        on: {
          NEXT: State.PRICING_PLAN,
          PREV: State.BUSINESS_INFO,
        },
      },

      [State.AGREEMENT]: {
        always: {
          cond: Guard.HasSignedAgreement,
          target: State.SEND_APPLICATION,
        },

        on: {
          NEXT: [
            {
              cond: Guard.HasSignedAgreement,
              target: State.SEND_APPLICATION,
            },
            {
              target: State.VERIFICATION,
            },
          ],
          PREV: {
            target: State.VERIFICATION,
          },
        },
      },

      [State.PRICING_PLAN]: {
        always: {
          cond: Guard.HasSelectedPlan,
          target: State.AGREEMENT,
        },
        on: {
          NEXT: [
            {
              cond: Guard.HasSelectedPlan,
              target: State.AGREEMENT,
            },
            {
              target: State.VERIFICATION,
            },
          ],
          PREV: {
            target: State.VERIFICATION,
          },
        },
      },

      [State.SEND_APPLICATION]: {
        invoke: {
          onDone: [
            {
              actions: [
                Action.TrackCustomerAcquisitionCompleted,
                shared.actions.track({
                  name: 'submitApplication',
                  properties: {
                    isCapitalDeposit: false,
                    isPrimaryApplicant: true,
                    isShineStart: false,
                    proofOfAddressSentByEmail: false,
                  },
                }),
              ],
              cond: Guard.IsAwaitingAccountValidation,
              target: State.REVIEW,
            },
            {
              actions: [
                shared.actions.toast({
                  status: 'error',
                  title: locales.toast.unknownError,
                }),
                () => {
                  logger.error(
                    `isAwaitingAccountValidation should be true after sending application`,
                  );
                },
              ],
              target: State.VERIFICATION,
            },
          ],

          onError: [
            {
              cond: Guard.IsKycNotReadyError,
              target: State.CHECK_IDENTITY_DOCUMENT,
            },
            {
              actions: shared.actions.error(),
              target: State.VERIFICATION,
            },
          ],

          src: Service.SubmitApplication,
        },
      },

      [State.CHECK_IDENTITY_DOCUMENT]: {
        on: {
          NEXT: State.VERIFICATION,
          PREV: State.VERIFICATION,
        },
      },

      [State.REVIEW]: {
        entry: [
          Action.TriggerOnboardingCompletedMessage,
          shared.actions.trackView(),
        ],

        on: {
          PREV: {
            actions: shared.actions.toast({
              status: 'error',
              title: locales.toast.definitiveStep,
            }),
          },
        },
      },

      [State.BENEFICIARIES_DOCS]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: [
            {
              cond: ({ getModuleData }) => {
                const { companyData } = getModuleData();

                if (!companyData) {
                  throw Error('No company in state BENEFICIARIES_DOCS');
                }

                return companyData.companyUser.isAwaitingAccountValidation;
              },
              target: State.REVIEW,
            },
            { target: State.VERIFICATION },
          ],
        },
      },

      [State.BENEFICIARIES_DOCS_INTRO]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.VERIFICATION,
        },
      },

      [State.CANNOT_OPEN_ACCOUNT]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: undefined,
        },
      },

      [State.DOWNLOAD_SHINE_MESSAGE_SENT]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: [
            {
              cond: Guard.IsMultiKYC,
              target: State.BENEFICIARIES_DOCS_INTRO,
            },
            {
              target: State.REVIEW,
            },
          ],
        },
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.ABORT_TO_CAPITAL_DEPOSIT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
